/**
 * Which of the six colour themes this device shows the app in. Purely
 * cosmetic — background, buttons, inputs, via a data-theme attribute that
 * style.css's tokens key off (see the top of that file). Never touches
 * scoring, and never snapshotted per game like house rules are: this is a
 * per-device preference, like branding.ts, so everyone at the table can
 * pick their own without affecting anyone else's screen.
 */

export type Theme = 'elevated' | 'bamboo' | 'felt' | 'sakura' | 'midnight' | 'ocean';

export interface ThemeInfo {
  id: Theme;
  name: string;
  description: string;
  /** [accent, background, tile-face] — a 3-dot preview swatch, and accent doubles as the theme-color meta tag. */
  chips: [string, string, string];
}

export const THEMES: ThemeInfo[] = [
  { id: 'elevated', name: 'Elevated', description: 'Sage & jade, ivory tiles', chips: ['#0f5132', '#e6ecd9', '#fbf7ea'] },
  { id: 'bamboo', name: 'Bamboo & Ivory', description: 'Warm, traditional', chips: ['#8a3a30', '#e9deba', '#fdf8ea'] },
  { id: 'felt', name: 'Jade Felt', description: 'Dramatic, premium', chips: ['#b6893c', '#0e3d29', '#faf6e9'] },
  { id: 'sakura', name: 'Sakura Blush', description: 'Soft, cheerful', chips: ['#c0455a', '#f9e7ea', '#fff7f8'] },
  { id: 'midnight', name: 'Midnight Ink', description: 'True dark mode', chips: ['#d9a441', '#15181a', '#f3ead2'] },
  { id: 'ocean', name: 'Ocean Breeze', description: 'Cool, coastal', chips: ['#0e7490', '#dcf0ee', '#f7fcfb'] },
];

const THEME_KEY = 'mahjong-scorer:theme';
const DEFAULT_THEME: Theme = 'elevated';

function isTheme(value: string | null): value is Theme {
  return THEMES.some((t) => t.id === value);
}

export function readTheme(): Theme {
  const stored = localStorage.getItem(THEME_KEY);
  return isTheme(stored) ? stored : DEFAULT_THEME;
}

export function writeTheme(theme: Theme): void {
  localStorage.setItem(THEME_KEY, theme);
}

/**
 * Paints <html> with the chosen theme's tokens and tints the browser/PWA
 * chrome to match. Call once at startup with the stored (or default) theme,
 * and again whenever the picker changes it.
 */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset['theme'] = theme;
  const accent = THEMES.find((t) => t.id === theme)?.chips[0];
  if (accent) {
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', accent);
  }
}
